import { Block, Spec, Chips, Prose, str, money, range, fmtDate, safeHref } from "./SubmissionBlocks";

/**
 * Valuation submission detail, at parity with the website studio's
 * /studio/valuations view (components/admin/Valuations.tsx in the website repo).
 *
 * Reads entirely from the verbatim `payload`, so a column added to
 * valuation_submissions upstream shows up here without a CRM migration.
 */

/**
 * The one reliable marker of a fallback (non-company-specific) valuation.
 *
 * Do NOT switch this to a date test. A note in the website repo once claimed
 * everything before 2026-04-10 was fallback output; the data says otherwise.
 * Genuine company-specific analysis exists from 2025-12-09, and five of the six
 * fallback rows are dated 2026-04-10 itself — test submissions made during the
 * debugging session that fixed the bug. Matching the template's opening line is
 * accurate whatever the date.
 */
const isFallbackAnalysis = (p: Record<string, unknown>) =>
  String(p.aiReasoning ?? "").startsWith("Conservative estimate based on");

const num = (v: unknown, suffix = "") => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? `${n}${suffix}` : null;
};

export function ValuationDetail({ payload: p }: { payload: Record<string, unknown> }) {
  const site = str(p.companyWebsite);
  // companyWebsite is free text on a public form. The previous
  // `startsWith("http")` normalisation happened to defuse `javascript:` by
  // prefixing it into a harmless `https://javascript:...`, but that was luck,
  // not a check. safeHref allowlists the scheme explicitly.
  const siteHref = safeHref(site);
  const hasAi = Boolean(p.aiReasoning || p.aiMethodology || p.aiMarketInsights);
  const fallback = isFallbackAnalysis(p);

  const multiple =
    num(p.impliedMultipleLow) || num(p.impliedMultipleHigh)
      ? `${num(p.impliedMultipleLow, "x") ?? "?"} – ${num(p.impliedMultipleHigh, "x") ?? "?"}`
      : null;

  return (
    <div className="space-y-4">
      {/* The three numbers worth knowing before a call. */}
      <div className="grid grid-cols-3 gap-3">
        {[
          ["Estimated value", range(p.valuationLow, p.valuationHigh)],
          ["Implied multiple", multiple],
          ["Revenue", money(p.revenueValue) ?? str(p.revenueRange)],
        ].map(([label, value]) => (
          <div key={label as string} className="rounded-lg border border-border p-3">
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className="mt-1 text-lg font-semibold tabular-nums">{value ?? "—"}</div>
          </div>
        ))}
      </div>

      <Block title="Who submitted it">
        <Spec label="Name" value={str(p.contactName)} />
        <Spec
          label="Email"
          value={
            str(p.contactEmail) ? (
              <a className="text-primary-text hover:underline" href={`mailto:${p.contactEmail}`}>
                {String(p.contactEmail)}
              </a>
            ) : null
          }
        />
        <Spec
          label="Phone"
          value={
            str(p.contactPhone) ? (
              <a className="text-primary-text hover:underline" href={`tel:${p.contactPhone}`}>
                {String(p.contactPhone)}
              </a>
            ) : null
          }
        />
        <Spec label="IP address" value={str(p.ipAddress)} mono />
      </Block>

      <Block
        title="Where it came from"
        note="Resolved from the submitter's IP. This identifies the network, not the person: a corporate ISP corroborates an employer, a residential one gives little beyond the metro, and VPNs defeat it entirely. Treat as corroboration, never identification."
      >
        <Spec
          label="Location"
          value={[p.geoCity, p.geoRegion, p.geoCountry].filter(Boolean).join(", ")}
        />
        <Spec label="Organization" value={str(p.geoOrg)} />
        <Spec label="Internet provider" value={str(p.geoIsp)} />
        <Spec label="Timezone" value={str(p.geoTimezone)} />
        <Spec
          label="Resolved"
          value={
            p.geoBackfilledAt ? `Looked up later on ${fmtDate(p.geoBackfilledAt)}` : "At submission"
          }
        />
      </Block>

      <Block title="Company">
        <Spec label="Name" value={str(p.companyName)} />
        <Spec
          label="Website"
          value={
            siteHref ? (
              <a
                className="text-primary-text hover:underline"
                href={siteHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {site}
              </a>
            ) : null
          }
        />
        <Spec label="Industry" value={str(p.industry)} />
        <Spec
          label="NAICS"
          value={
            str(p.naicsCode)
              ? `${p.naicsCode}${p.naicsDescription ? ` · ${p.naicsDescription}` : ""}`
              : null
          }
        />
        <Spec label="Years in business" value={str(p.yearsInBusiness)} />
      </Block>

      <Block title="What they told us">
        <Spec label="Revenue range" value={str(p.revenueRange)} />
        <Spec label="Income type" value={str(p.incomeType)} />
        <Spec label="Net income / EBITDA" value={str(p.netIncomeRange)} />
        <Spec label="Owner compensation" value={str(p.ownerCompensation)} />
        <Spec label="Growth trend" value={str(p.growthTrend)} />
        <Spec label="Recurring revenue" value={str(p.recurringRevenue)} />
        <Spec label="Customer concentration" value={str(p.customerConcentration)} />
        <Spec label="Competitive advantage" value={str(p.competitiveAdvantage)} />
        <Spec label="Owner involvement" value={str(p.ownerInvolvement)} />
        <Spec label="Competitive position" value={str(p.competitivePosition)} />
      </Block>

      {hasAi && (
        <Block title="AI analysis">
          {fallback && (
            <div className="mb-3 rounded border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-700 dark:text-amber-400">
              Fallback estimate — generic, not company-specific analysis.
            </div>
          )}
          <div className="space-y-3">
            <div>
              <div className="mb-1 text-xs text-muted-foreground">Reasoning</div>
              <Prose text={p.aiReasoning} />
            </div>
            <div>
              <div className="mb-1 text-xs text-muted-foreground">Methodology</div>
              <Prose text={p.aiMethodology} />
            </div>
            <div>
              <div className="mb-1 text-xs text-muted-foreground">Market insights</div>
              <Prose text={p.aiMarketInsights} />
            </div>
            <Spec label="Key factors" value={<Chips values={p.aiKeyFactors} />} />
            <Spec label="Value drivers" value={<Chips values={p.aiValueDrivers} />} />
            <Spec label="Risk factors" value={<Chips values={p.aiRiskFactors} />} />
          </div>
        </Block>
      )}
    </div>
  );
}
